import { create } from 'zustand';
import { getViewDefaultFilters } from '../utils/filterComparison';
import type { ActiveFilter, Evidence, View, PendingAction } from './types';

interface EvidencesStore {
  // UI State (replaces Context)
  activeFilters: ActiveFilter[];
  selectedView: View | null;
  page: string;
  search: string;
  sortStatus: { columnAccessor: string; direction: 'asc' | 'desc' };
  
  // List State (for optimistic updates)
  evidences: Evidence[];
  totalCount: number;
  isLoading: boolean;
  
  // Modal State
  openedEvidence: Evidence | null;
  isModalOpen: boolean;
  
  // View counts for tabs
  viewCounts: Record<string, number>;
  
  // Pending actions (for rollback)
  pendingActions: Map<string, PendingAction>;
  
  // UI State Actions
  setActiveFilters: (filters: ActiveFilter[]) => void;
  clearFilters: () => void;
  removeFilter: (filterName: string) => void;
  updateFilter: (filterName: string, updates: Partial<ActiveFilter>) => void;
  setSelectedView: (view: View | null) => void;
  setPage: (page: string) => void;
  setSearch: (search: string) => void;
  setSortStatus: (status: { columnAccessor: string; direction: 'asc' | 'desc' }) => void;
  resetToPageOne: () => void;
  
  // List State Actions
  setEvidences: (evidences: Evidence[], totalCount: number) => void;
  setLoading: (loading: boolean) => void;
  optimisticRemoveEvidence: (evidenceId: string, actionType?: 'assign' | 'dismiss' | 'backToInbox' | 'unassign') => void;
  optimisticUpdateEvidence: (evidenceId: string, updates: Partial<Evidence>) => void;
  rollbackEvidence: (evidenceId: string, originalEvidence: Evidence) => void;
  
  // Modal Actions
  openEvidenceModal: (evidence: Evidence) => void;
  closeModal: () => void;
  updateOpenedEvidence: (updates: Partial<Evidence>) => void;
  
  // View Count Actions
  setViewCounts: (counts: Record<string, number>) => void;
  decrementViewCount: (viewId?: string) => void;
  incrementViewCount: (viewId?: string) => void;
  
  // Helper
  getCurrentActiveFilters: () => ActiveFilter[];
}

export const useEvidencesStore = create<EvidencesStore>((set, get) => ({
  // Initial State
  activeFilters: [],
  selectedView: null,
  page: '1',
  search: '',
  sortStatus: { columnAccessor: 'createdAt', direction: 'desc' },
  evidences: [],
  totalCount: 0,
  isLoading: false,
  openedEvidence: null,
  isModalOpen: false,
  viewCounts: {},
  pendingActions: new Map(),
  
  // UI State Actions
  setActiveFilters: (filters) => {
    console.log('🏪 Store setActiveFilters called. Filters:', filters?.length);
    // Don't clear evidences here - let the new data replace them when it arrives
    // This prevents flash of "No evidences found" during loading
    set({ activeFilters: filters });
    get().resetToPageOne();
  },
  
  clearFilters: () => {
    const { selectedView } = get();
    set((state) => {
      if (selectedView?.filters && Array.isArray(selectedView.filters)) {
        // Keep only the filters that match view filters exactly
        const viewDefaultFilters = getViewDefaultFilters(state.activeFilters, selectedView.filters);
        return { activeFilters: viewDefaultFilters };
      } else {
        return { activeFilters: [] };
      }
    });
    get().resetToPageOne();
  },
  
  removeFilter: (filterName) => {
    set((state) => ({
      activeFilters: state.activeFilters.filter((filter) => filter.name !== filterName),
    }));
    get().resetToPageOne();
  },
  
  updateFilter: (filterName, updates) => {
    set((state) => ({
      activeFilters: state.activeFilters.map((filter) =>
        filter.name === filterName ? { ...filter, ...updates } : filter
      ),
    }));
    get().resetToPageOne();
  },
  
  setSelectedView: (view) => {
    console.log('🏪 Store setSelectedView called. View:', view?.name || view?._id);
    // Don't clear evidences here - let the new data replace them when it arrives
    set({ selectedView: view });
    // Apply view filters if they exist
    if (view?.filters && Array.isArray(view.filters) && view.filters.length > 0) {
      console.log('🏪 Store setSelectedView applying view filters:', view.filters?.length);
      set({ activeFilters: view.filters });
    } else {
      console.log('🏪 Store setSelectedView clearing filters');
      set({ activeFilters: [] });
    }
    get().resetToPageOne();
  },
  
  setPage: (page) => set({ page }),
  setSearch: (search) => {
    set({ search });
    get().resetToPageOne();
  },
  setSortStatus: (sortStatus) => {
    set({ sortStatus });
    get().resetToPageOne();
  },
  resetToPageOne: () => set({ page: '1' }),
  
  // List State Actions
  setEvidences: (evidences, totalCount) => {
    console.log('🏪 Store setEvidences called:', evidences?.length, 'total:', totalCount);
    set({ evidences, totalCount });
  },
  setLoading: (loading) => set({ isLoading: loading }),
  
  optimisticRemoveEvidence: (evidenceId, actionType = 'assign') => {
    const state = get();
    const evidence = state.evidences.find((e) => e._id === evidenceId);
    
    if (!evidence) return;
    
    // Store original for potential rollback
    const pendingAction: PendingAction = {
      evidenceId,
      originalEvidence: { ...evidence },
      actionType,
      timestamp: Date.now(),
    };
    
    const newPendingActions = new Map(state.pendingActions);
    newPendingActions.set(evidenceId, pendingAction);
    
    // Remove from list and decrement count
    set({
      evidences: state.evidences.filter((e) => e._id !== evidenceId),
      totalCount: Math.max(0, state.totalCount - 1),
      pendingActions: newPendingActions,
    });
    
    // Decrement view count if applicable
    if (state.selectedView?._id) {
      get().decrementViewCount(state.selectedView._id);
    }
  },
  
  optimisticUpdateEvidence: (evidenceId, updates) => {
    set((state) => ({
      evidences: state.evidences.map((ev) =>
        ev._id === evidenceId ? { ...ev, ...updates } : ev
      ),
      // Also update opened evidence if it's the same
      openedEvidence:
        state.openedEvidence?._id === evidenceId
          ? { ...state.openedEvidence, ...updates }
          : state.openedEvidence,
    }));
  },
  
  rollbackEvidence: (evidenceId, originalEvidence) => {
    const state = get();
    const pendingAction = state.pendingActions.get(evidenceId);
    
    if (!pendingAction) return;
    
    // Restore evidence to list
    const evidences = [...state.evidences];
    // Insert at original position if we can determine it, otherwise at the end
    evidences.push(originalEvidence);
    
    const newPendingActions = new Map(state.pendingActions);
    newPendingActions.delete(evidenceId);
    
    set({
      evidences,
      totalCount: state.totalCount + 1,
      pendingActions: newPendingActions,
    });
    
    // Increment view count if applicable
    if (state.selectedView?._id) {
      get().incrementViewCount(state.selectedView._id);
    }
  },
  
  // Modal Actions
  openEvidenceModal: (evidence) => {
    set({
      openedEvidence: evidence,
      isModalOpen: true,
    });
  },
  
  closeModal: () => {
    set({
      openedEvidence: null,
      isModalOpen: false,
    });
  },
  
  updateOpenedEvidence: (updates) => {
    const state = get();
    const updatedEvidence = state.openedEvidence
      ? { ...state.openedEvidence, ...updates }
      : null;
    
    set({ openedEvidence: updatedEvidence });
    
    // Also update in list if it exists there
    if (updates && state.openedEvidence?._id) {
      get().optimisticUpdateEvidence(state.openedEvidence._id, updates);
    }
  },
  
  // View Count Actions
  setViewCounts: (counts) => set({ viewCounts: counts }),
  
  decrementViewCount: (viewId) => {
    if (!viewId) return;
    set((state) => ({
      viewCounts: {
        ...state.viewCounts,
        [viewId]: Math.max(0, (state.viewCounts[viewId] || 0) - 1),
      },
    }));
  },
  
  incrementViewCount: (viewId) => {
    if (!viewId) return;
    set((state) => ({
      viewCounts: {
        ...state.viewCounts,
        [viewId]: (state.viewCounts[viewId] || 0) + 1,
      },
    }));
  },
  
  // Helper
  getCurrentActiveFilters: () => get().activeFilters,
}));

